import React, { forwardRef } from 'react';
import SignedImage from '@/components/ipe/SignedImage';
import {
  appendViewVariant,
  isLoadableDisplayPreviewUrl,
  pickImageSignOptions,
} from '@/utils/imageDisplayAccess';
import { IPE_SOURCING_MATERIAL_IMAGE_ROOT_MARGIN } from '@/constants/lazyLoadConfig';

function isDirectPreviewImageUrl(raw) {
  const s = String(raw || '').trim();
  if (!s) return false;
  return s.startsWith('data:')
    || s.startsWith('blob:')
    || s.startsWith('/images/')
    || s.startsWith('/assets/');
}

/**
 * 工作台列表/卡片预览：与图片空间一致——
 * - 直链/data/blob：立即加载；
 * - 受管存储：视口内加载 + 已加载 URL 内存缓存（见 SignedImage / useManagedViewImageSrc），不叠 native lazy。
 * displayUrl 为 blob/data 时优先直出，避免受管 url 仍走签图 view 打满 429。
 *
 * 商品模板列表：接口已 enrich 的 previewUrlDisplay（HMAC/OSS）在 loading=eager 时直出 &lt;img&gt;，
 * 跳过 lazy 队列 / thumb 换签，避免「Network 有路径、封面仍白底」。
 */
const WorkbenchPreviewImage = forwardRef(function WorkbenchPreviewImage({
  url,
  displayUrl,
  viewUrl,
  record = null,
  alt = '',
  loading,
  variant = 'thumb',
  viewRoot = null,
  viewRootMargin = IPE_SOURCING_MATERIAL_IMAGE_ROOT_MARGIN,
  decoding = 'async',
  ...imgProps
}, ref) {
  const raw = String(url || '').trim();
  const explicitDisplay = String(displayUrl || viewUrl || '').trim();
  const preferredDisplay = explicitDisplay
    || (isLoadableDisplayPreviewUrl(raw) ? raw : '');

  if (isDirectPreviewImageUrl(preferredDisplay)) {
    return (
      <img
        ref={ref}
        src={preferredDisplay}
        alt={alt}
        loading={loading ?? 'lazy'}
        decoding={decoding}
        {...imgProps}
      />
    );
  }
  if (!raw && !preferredDisplay) return null;
  if (isDirectPreviewImageUrl(raw)) {
    return (
      <img
        ref={ref}
        src={raw}
        alt={alt}
        loading={loading ?? 'eager'}
        decoding={decoding}
        {...imgProps}
      />
    );
  }

  // 已显式签发的 displayUrl/viewUrl + eager：直出，避免 IntersectionObserver/队列卡在占位。
  // 注意：不能把「裸 /uploads/ 也被 isLoadableDisplayPreviewUrl 放行」当成可直出——
  // 受管存储必须先签图，否则会 403 裂图（复色弹窗印花/主图就是这样挂的）。
  if (
    loading === 'eager'
    && explicitDisplay
    && isLoadableDisplayPreviewUrl(explicitDisplay)
  ) {
    const src = appendViewVariant(explicitDisplay, variant) || explicitDisplay;
    return (
      <img
        ref={ref}
        src={src}
        alt={alt}
        loading="eager"
        decoding={decoding}
        {...imgProps}
      />
    );
  }

  if (!raw) return null;

  // 勿把模板行 id（IMG-…）当作 imageId 写进签图缓存，避免与图片空间 id 碰撞
  const signRecord = record && typeof record === 'object'
    ? {
      ...record,
      id: record.previewImageId || record.imageId || undefined,
    }
    : record;

  const signOptions = {
    ...pickImageSignOptions(signRecord),
    displayUrl: preferredDisplay || displayUrl || signRecord?.displayUrl,
    viewUrl: viewUrl
      || (isLoadableDisplayPreviewUrl(raw) ? raw : undefined)
      || signRecord?.viewUrl,
    storedUrlDisplay: signRecord?.storedUrlDisplay,
    storedViewUrl: signRecord?.storedViewUrl,
  };
  return (
    <SignedImage
      ref={ref}
      url={raw}
      record={signRecord}
      {...signOptions}
      alt={alt}
      loading={loading}
      variant={variant}
      viewRoot={viewRoot}
      viewRootMargin={viewRootMargin}
      decoding={decoding}
      {...imgProps}
    />
  );
});

export default WorkbenchPreviewImage;
